import { Collapse, Select, Tooltip } from 'antd'
import clsx from 'clsx'
import { useMemo, type FC } from 'react'

import { Alert, Button } from '@/components/elements'
import { SHIPMENT_ORDER_CSV_IMPORT } from '@/constants/common/message.constants'

import {
  LOGI_FIELDS,
  type LogiFieldValue,
  type MasterCandidatesByField
} from '../../models'

import {
  SUNTORY_EDITABLE_FIELDS,
  type SuntoryCheck,
  type SuntoryOverrides
} from './useSuntoryImport'

type Props = {
  check: SuntoryCheck
  /** 確認画面で直した値（行番号 → 項目 → 値） */
  overrides: SuntoryOverrides
  /** 直した値で確かめ直している間は true */
  isRechecking: boolean
  /** 直せる項目の選択肢（汎用の確認画面の一括入力と同じ候補） */
  masterCandidates: MasterCandidatesByField
  onEditCell: (rowNo: number, field: LogiFieldValue, value: string) => void
  /** 修正箇所の件数（直す前と値が変わったセルの数）。0 ならボタンを出さない */
  changeCount: number
  /** 修正箇所の一覧を CSV でダウンロードする */
  onDownloadChanges: () => void
  /** 修正を反映した CSV を出せるか（直したところがあり、選んだファイルが手元にある） */
  canDownloadImported: boolean
  /** 修正を反映した CSV（元の CSV の全行に、荷主・発地・着地の直しを書き戻したもの）をダウンロードする */
  onDownloadImported: () => void
}

/**
 * サントリー様の CSV の確認画面。事前チェック API が読んだ行を、取り込まれる項目ごとに並べ、
 * 問題のある行は赤くしてその行の問題を右端に出す。
 * マスタに無いエラーのセル（荷主・配送種別・発着地・荷姿・車種・パートナー）は、その場で選び直せる。
 * 読み取りの段階で落ちた行は中身が無いので、ファイルを直す。行を見分けられるように明細ID・届先・品目と
 * CSV の行番号を出し、列ごとに入っていた値と何なら通るかを出す。読み飛ばした行（配車NO が空・PAL- 品目）は表の下に件数と理由を出す。
 */
export const SuntoryPreviewView: FC<Props> = ({
  check,
  overrides,
  isRechecking,
  masterCandidates,
  onEditCell,
  changeCount,
  onDownloadChanges,
  canDownloadImported,
  onDownloadImported
}) => {
  const table = useMemo(() => {
    if (check.status !== 'done') return null
    const rows = check.result.rows ?? []
    const issuesByRow = new Map<number, string[]>()
    const issueFieldsByRow = new Map<number, Set<LogiFieldValue>>()
    const fileIssues: string[] = []
    for (const issue of check.result.issues) {
      if (issue.rowNo === null) {
        fileIssues.push(issue.message)
        continue
      }
      issuesByRow.set(issue.rowNo, [
        ...(issuesByRow.get(issue.rowNo) ?? []),
        issue.message
      ])
      if (issue.field) {
        const fields = issueFieldsByRow.get(issue.rowNo) ?? new Set()
        fields.add(issue.field as LogiFieldValue)
        issueFieldsByRow.set(issue.rowNo, fields)
      }
    }
    // 値のある項目に加えて、エラーや直した値のある項目も列に出す（空のセルを選び直せるように）
    const present = new Set<string>([
      ...rows.flatMap((r) => r.cells.map((c) => c.field)),
      ...[...issueFieldsByRow.values()].flatMap((fields) => [...fields]),
      ...Object.values(overrides).flatMap((cells) => Object.keys(cells))
    ])
    const fields = LOGI_FIELDS.filter((f) => present.has(f.value))
    const readFailureByRow = new Map(
      (check.result.readFailures ?? []).map((f) => [f.rowNo, f])
    )
    const skippedRows = check.result.skippedRows ?? []
    return {
      rows,
      fields,
      issuesByRow,
      issueFieldsByRow,
      fileIssues,
      readFailureByRow,
      skippedRows
    }
  }, [check, overrides])

  if (check.status === 'checking' || check.status === 'idle') {
    return (
      <span
        className="text-xs text-gray-2"
        data-testid="shipment-order-csv-import__suntory-checking"
      >
        {SHIPMENT_ORDER_CSV_IMPORT.SUNTORY_CHECKING}
      </span>
    )
  }
  if (check.status === 'failed' || !table) {
    return (
      <Alert
        color="red"
        title={SHIPMENT_ORDER_CSV_IMPORT.SUNTORY_CHECK_FAILED}
        data-testid="shipment-order-csv-import__suntory-check-failed"
      >
        {null}
      </Alert>
    )
  }

  const {
    rows,
    fields,
    issuesByRow,
    issueFieldsByRow,
    fileIssues,
    readFailureByRow,
    skippedRows
  } = table
  const hasIssues = check.result.issues.length > 0

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <span
          className="text-xs text-secondary"
          data-testid="shipment-order-csv-import__suntory-summary"
        >
          {SHIPMENT_ORDER_CSV_IMPORT.SUNTORY_PREVIEW_SUMMARY(
            rows.length,
            // ファイル全体の問題も 1 件として数える（赤い見出しと件数を食い違わせない）
            issuesByRow.size + fileIssues.length
          )}
        </span>
        {isRechecking && (
          <span
            className="text-xs text-gray-2"
            data-testid="shipment-order-csv-import__suntory-rechecking"
          >
            {SHIPMENT_ORDER_CSV_IMPORT.SUNTORY_RECHECKING}
          </span>
        )}
        {canDownloadImported && (
          <Tooltip
            title={SHIPMENT_ORDER_CSV_IMPORT.SUNTORY_DOWNLOAD_IMPORTED_HINT}
          >
            <Button
              size="small"
              className="ml-auto"
              onClick={onDownloadImported}
              data-testid="shipment-order-csv-import__suntory-download-imported"
            >
              {SHIPMENT_ORDER_CSV_IMPORT.SUNTORY_DOWNLOAD_IMPORTED}
            </Button>
          </Tooltip>
        )}
        {changeCount > 0 && (
          <Tooltip
            title={SHIPMENT_ORDER_CSV_IMPORT.SUNTORY_DOWNLOAD_CHANGES_HINT}
          >
            <Button
              size="small"
              className={canDownloadImported ? undefined : 'ml-auto'}
              onClick={onDownloadChanges}
              data-testid="shipment-order-csv-import__suntory-download-changes"
            >
              {SHIPMENT_ORDER_CSV_IMPORT.SUNTORY_DOWNLOAD_CHANGES(changeCount)}
            </Button>
          </Tooltip>
        )}
      </div>
      {hasIssues && (
        // ファイル全体の問題（形式違い等）は、取り込めない理由そのものを赤い見出しにする。
        // 行の問題向けの「赤いセルを選び直せる」案内を見出しにすると、理由の文が埋もれて読まれないため（#5511）。
        // 見出し（title）は div の title 属性と型が重なり文字列しか渡せないので、2 件目以降の理由は本文に出す
        <Alert
          color="red"
          title={
            fileIssues[0] ??
            SHIPMENT_ORDER_CSV_IMPORT.SUNTORY_PREVIEW_HAS_ERRORS
          }
          data-testid="shipment-order-csv-import__suntory-has-errors"
        >
          {(fileIssues.length > 1 || issuesByRow.size > 0) &&
            fileIssues.length > 0 && (
              <div className="flex flex-col text-xs">
                {fileIssues.slice(1).map((message, i) => (
                  <span key={i} className="font-bold text-red">
                    {message}
                  </span>
                ))}
                {issuesByRow.size > 0 && (
                  <span>
                    {SHIPMENT_ORDER_CSV_IMPORT.SUNTORY_PREVIEW_HAS_ERRORS}
                  </span>
                )}
              </div>
            )}
        </Alert>
      )}
      {rows.length === 0 ? (
        <span className="text-xs text-gray-2">
          {SHIPMENT_ORDER_CSV_IMPORT.SUNTORY_PREVIEW_EMPTY}
        </span>
      ) : (
        <div className="overflow-auto rounded border border-table-border">
          <table
            className="w-full text-xs"
            data-testid="shipment-order-csv-import__suntory-table"
          >
            <thead className="bg-neutral-3 text-left">
              <tr>
                <th className="whitespace-nowrap px-2 py-1">
                  {SHIPMENT_ORDER_CSV_IMPORT.PREVIEW_ROW_NO_HEADER}
                </th>
                {fields.map((f) => (
                  <th key={f.value} className="whitespace-nowrap px-2 py-1">
                    {f.label}
                  </th>
                ))}
                <th className="whitespace-nowrap px-2 py-1">
                  {SHIPMENT_ORDER_CSV_IMPORT.SUNTORY_PREVIEW_ERROR_HEADER}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const issues = issuesByRow.get(row.rowNo) ?? []
                const readFailure = readFailureByRow.get(row.rowNo)
                const issueFields = issueFieldsByRow.get(row.rowNo)
                const edited = overrides[row.rowNo] ?? {}
                // 読み取りで落ちた行は中身が無い（直せない）
                const isReadable = row.cells.length > 0
                const valueOf = new Map(
                  row.cells.map((c) => [c.field, c.value])
                )
                return (
                  <tr
                    key={row.rowNo}
                    className={clsx(
                      'border-t border-table-border',
                      issues.length > 0 && 'bg-[#ffccc7]'
                    )}
                    data-testid={`shipment-order-csv-import__suntory-row-${row.rowNo}`}
                  >
                    <td
                      className={clsx(
                        'px-2 py-1',
                        issues.length > 0 && 'font-medium text-red'
                      )}
                    >
                      {row.rowNo}
                    </td>
                    {fields.map((f) => {
                      const isEdited = edited[f.value] !== undefined
                      const hasIssue = issueFields?.has(f.value) ?? false
                      const isEditable =
                        isReadable &&
                        SUNTORY_EDITABLE_FIELDS.has(f.value) &&
                        (hasIssue || isEdited)
                      const value =
                        edited[f.value] ?? valueOf.get(f.value) ?? ''
                      return (
                        <td
                          key={f.value}
                          className={clsx(
                            'whitespace-nowrap px-2 py-1',
                            isEdited && !hasIssue && 'bg-[#e6f4ff]'
                          )}
                          data-testid={`shipment-order-csv-import__suntory-cell-${row.rowNo}-${f.value}`}
                        >
                          {isEditable ? (
                            <Select
                              size="small"
                              className="w-56"
                              showSearch
                              status={hasIssue ? 'error' : undefined}
                              value={value || undefined}
                              options={(masterCandidates[f.value] ?? []).map(
                                (m) => ({
                                  value: m.code,
                                  label: `${m.code}/${m.name}`
                                })
                              )}
                              filterOption={(input, option) =>
                                !!option?.label &&
                                option.label
                                  .toString()
                                  .toLowerCase()
                                  .includes(input.toLowerCase())
                              }
                              onChange={(next: string) =>
                                onEditCell(row.rowNo, f.value, next ?? '')
                              }
                              data-testid={`shipment-order-csv-import__suntory-edit-${row.rowNo}-${f.value}`}
                            />
                          ) : (
                            value
                          )}
                        </td>
                      )
                    })}
                    {/* 読み取りで落ちた行の理由は長いので、細い列で縦に折り返さないよう幅を取る */}
                    <td
                      className="min-w-[360px] px-2 py-1 text-red"
                      data-testid={`shipment-order-csv-import__suntory-row-issues-${row.rowNo}`}
                    >
                      {readFailure ? (
                        <div className="flex flex-col">
                          <span className="font-medium">
                            {SHIPMENT_ORDER_CSV_IMPORT.SUNTORY_READ_FAILURE_IDENTITY(
                              readFailure.line,
                              readFailure.detailId,
                              readFailure.destName,
                              readFailure.itemName
                            )}
                          </span>
                          {readFailure.details.map((detail, i) => (
                            <span key={i}>
                              {SHIPMENT_ORDER_CSV_IMPORT.SUNTORY_READ_FAILURE_DETAIL(
                                detail.column,
                                detail.value,
                                detail.expected
                              )}
                            </span>
                          ))}
                        </div>
                      ) : (
                        issues.join(' / ')
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
      {skippedRows.length > 0 && (
        <Collapse
          size="small"
          items={[
            {
              key: 'skipped',
              label: (
                <span
                  className="text-xs text-secondary"
                  data-testid="shipment-order-csv-import__suntory-skipped-title"
                >
                  {SHIPMENT_ORDER_CSV_IMPORT.SUNTORY_SKIPPED_TITLE(
                    skippedRows.length
                  )}
                </span>
              ),
              children: (
                <table
                  className="w-full text-xs"
                  data-testid="shipment-order-csv-import__suntory-skipped"
                >
                  <thead className="bg-neutral-3 text-left">
                    <tr>
                      {[
                        SHIPMENT_ORDER_CSV_IMPORT.SUNTORY_SKIPPED_LINE_HEADER,
                        SHIPMENT_ORDER_CSV_IMPORT.SUNTORY_SKIPPED_REASON_HEADER,
                        SHIPMENT_ORDER_CSV_IMPORT.SUNTORY_SKIPPED_DETAIL_ID_HEADER,
                        SHIPMENT_ORDER_CSV_IMPORT.SUNTORY_SKIPPED_DEST_HEADER,
                        SHIPMENT_ORDER_CSV_IMPORT.SUNTORY_SKIPPED_ITEM_HEADER
                      ].map((header) => (
                        <th
                          key={header}
                          className="whitespace-nowrap px-2 py-1"
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {skippedRows.map((skipped) => (
                      <tr
                        key={skipped.line}
                        className="border-t border-table-border"
                        data-testid={`shipment-order-csv-import__suntory-skipped-${skipped.line}`}
                      >
                        <td className="px-2 py-1">{skipped.line}</td>
                        <td className="px-2 py-1">
                          {
                            SHIPMENT_ORDER_CSV_IMPORT.SUNTORY_SKIP_REASON[
                              skipped.reason
                            ]
                          }
                        </td>
                        <td className="px-2 py-1">{skipped.detailId ?? ''}</td>
                        <td className="px-2 py-1">{skipped.destName ?? ''}</td>
                        <td className="px-2 py-1">{skipped.itemName ?? ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
            }
          ]}
        />
      )}
    </div>
  )
}
